import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { filter, take } from 'rxjs';
import { DistrictService, DistrictDto, CreateDistrictDto } from '../../services/district.service';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-districts',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './districts.component.html',
  styleUrl: './districts.component.css'
})
export class DistrictsComponent implements OnInit {
  districts: DistrictDto[] = [];
  filteredDistricts: DistrictDto[] = [];

  isLoading = false;
  showCreateForm = false;
  isSubmitting = false;

  searchQuery = '';

  districtData: CreateDistrictDto = { name: '' };
  formErrors: { [key: string]: string } = {};

  notification = {
    show: false,
    message: '',
    type: 'success' as 'success' | 'error' | 'info'
  };

  constructor(
    private districtService: DistrictService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.authService.getMsalInitializedObservable()
      .pipe(
        filter(initialized => initialized),
        take(1)
      )
      .subscribe(() => this.loadDistricts());
  }

  loadDistricts(): void {
    this.isLoading = true;
    this.districtService.getDistricts().subscribe({
      next: (districts) => {
        this.districts = districts;
        this.applyFilter();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading districts:', error);
        this.showNotification('Failed to load districts. Please try again.', 'error');
        this.isLoading = false;
      }
    });
  }

  applyFilter(): void {
    const query = this.searchQuery.trim().toLowerCase();
    this.filteredDistricts = query
      ? this.districts.filter(d => d.name.toLowerCase().includes(query))
      : [...this.districts];
  }

  onSearchChange(): void {
    this.applyFilter();
  }

  toggleCreateForm(): void {
    this.showCreateForm = !this.showCreateForm;
    if (!this.showCreateForm) {
      this.resetForm();
    }
  }

  validateForm(): boolean {
    this.formErrors = {};
    if (!this.districtData.name?.trim()) {
      this.formErrors['name'] = 'District name is required';
    } else if (this.districtData.name.trim().length < 2) {
      this.formErrors['name'] = 'Must be at least 2 characters';
    } else if (this.districtData.name.trim().length > 100) {
      this.formErrors['name'] = 'Must be less than 100 characters';
    }
    return Object.keys(this.formErrors).length === 0;
  }

  onSubmit(): void {
    if (!this.validateForm()) return;

    this.isSubmitting = true;
    const payload: CreateDistrictDto = {
      name: this.districtData.name.trim()
    };

    this.districtService.createDistrict(payload).subscribe({
      next: () => {
        this.showNotification('District created successfully!', 'success');
        this.resetForm();
        this.showCreateForm = false;
        this.loadDistricts();
      },
      error: (error) => {
        console.error('Error creating district:', error);
        this.handleServerErrors(error);
        this.isSubmitting = false;
      }
    });
  }

  private handleServerErrors(error: any): void {
    this.formErrors = {};
    if (error?.error?.errors) {
      Object.keys(error.error.errors).forEach(key => {
        const fieldName = key.toLowerCase();
        this.formErrors[fieldName] = Array.isArray(error.error.errors[key])
          ? error.error.errors[key][0]
          : error.error.errors[key];
      });
    } else if (error?.error?.message) {
      this.formErrors['general'] = error.error.message;
    } else {
      this.formErrors['general'] = 'An unexpected error occurred. Please try again.';
    }
  }

  onCancelCreate(): void {
    this.showCreateForm = false;
    this.resetForm();
  }

  private resetForm(): void {
    this.districtData = { name: '' };
    this.formErrors = {};
    this.isSubmitting = false;
  }

  hasError(field: string): boolean {
    return !!this.formErrors[field];
  }

  getError(field: string): string {
    return this.formErrors[field] || '';
  }

  showNotification(message: string, type: 'success' | 'error' | 'info' = 'success'): void {
    this.notification = { show: true, message, type };
    setTimeout(() => {
      if (this.notification.message === message) {
        this.notification.show = false;
      }
    }, 3000);
  }

  closeNotification(): void {
    this.notification.show = false;
  }
}
